import { FC, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ENV_CONFIG } from 'config/environment';
import { getIsSuper } from 'config/permissions';
import { TextField } from 'components/Forms/TextField';
import { DescriptionField } from 'components/Forms/DescriptionField';
import { useAppSelector } from 'helpers/hooks';
import { parseCurrency, required, transformToUrl, formatNumberAsCurrency } from 'helpers/utils';
import { getPagesState } from 'selectors/pages';
import { getActiveRole } from 'selectors/admins';
import { IPage } from 'store/pages/types';
import { UploadFileForm } from '../Forms/UploadFileForm';
import { ColorPickerForm } from '../Forms/ColorPickerForm';
import { RecipientCardsForm } from '../Forms/RecipientCardsForm';
import { DonationAmountsForm } from '../Forms/DonationAmountsForm';
import { validateWebsiteUrl } from '../utils';
import { RadioField } from 'components/Forms/RadioField';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';
import {
  displayFundraisingBarOptions,
  recurringIntervalOptions,
  whiteLabelNavigationOptions,
} from '../constants';
import { AccordionButton } from './AccordionButton';
import { useFieldRestrictions } from 'hooks/useFieldRestrictions';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { useFieldDirty, useFieldValue } from 'hooks/useFormHelpers';
import { OfflineDonationForm } from '../Forms/OfflineDonationForm';
import { DonationAllocationForm } from '../Forms/DonationAllocation/DonationAllocationForm';
import { CUSTOM_ALLOCATION_TYPES } from '../Forms/DonationAllocation/DonationAllocationForm/constants';

type Props = {
  index: number;
  itemVisible: number;
  handleClickAccordion: (index: number) => void;
  isPreviewVisible: boolean;
  formState: any;
  editPageSelected?: IPage;
  isPageDuplicated: boolean;
};

export const DonatePageAccordion: FC<Props> = ({
  index,
  itemVisible,
  handleClickAccordion,
  isPreviewVisible,
  formState,
  editPageSelected,
  isPageDuplicated,
}) => {
  const pagesState = useAppSelector(getPagesState);
  const activeRole = useAppSelector(getActiveRole);
  const fieldRestrictions = useFieldRestrictions(!!editPageSelected);
  const [isQueryStringModalOpen, setIsQueryStringModalOpen] = useState(false);
  const [isQueryStringChangeConfirmed, setIsQueryStringChangeConfirmed] = useState(false);
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [isRecurringAllocationConflictModalOpen, setIsRecurringAllocationConflictModalOpen] =
    useState(false);

  const queryStringDirty = useFieldDirty('query_string');
  const optionalMediaType = useFieldValue('optional_media_type') as
    | 'image'
    | 'video'
    | null
    | undefined;

  const createFieldValidator = (validatorFn: any) => {
    return useMemo(() => {
      return (value: any) => {
        const stringValue = typeof value === 'string' ? value : value?.target?.value || '';
        return validatorFn(stringValue, pagesState, editPageSelected, isPageDuplicated);
      };
    }, [pagesState, editPageSelected, isPageDuplicated]);
  };

  const websiteUrlValidator = createFieldValidator(validateWebsiteUrl);

  const handleQueryStringBlur = () => {
    if (queryStringDirty && !isQueryStringChangeConfirmed && editPageSelected) {
      setIsQueryStringModalOpen(true);
    }
  };

  const optionalFileArrangementOptions = [
    { value: 'BELOW_PAGE_NAME', label: 'Below page name' },
    { value: 'BELOW_PAGE_DESCRIPTION', label: 'Below page description' },
  ];

  return (
    <div
      className={classNames('accordion-content', {
        'accordion-content--visible': itemVisible === index,
        'accordion-content--hidden': itemVisible !== index,
      })}
    >
      {index === 0 && (
        <div className="card">
          <ConfirmationModal
            isOpen={isQueryStringModalOpen}
            handleSave={() => {
              setIsQueryStringChangeConfirmed(true);
              setIsQueryStringModalOpen(false);
            }}
            handleCancel={() => {
              formState.change('query_string', formState.getState().initialValues?.query_string);
              setIsQueryStringModalOpen(false);
            }}
            warningMessage="Are you sure you want to change this page's URL? If this link has already been distributed to donors, we'd recommend to make a copy of this page to update the URL and pause this page with a redirect to the new URL."
          />
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Left-Side Panel</span>
            {fieldRestrictions.canSee('page_name') && (
              <TextField
                label="Page Name *"
                name="page_name"
                type="text"
                validate={required}
                disabled={fieldRestrictions.isDisabled('page_name')}
              />
            )}
            <div className="row mb8">
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <UploadFileForm type="tag" />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <ColorPickerForm
                  bgColorFieldName="page_background"
                  textColorFieldName="page_color_text"
                />
              </div>
            </div>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <UploadFileForm
                  type="optional-media"
                  initialFileType={optionalMediaType ?? undefined}
                />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <RadioField
                  name="optional_file_arrangement"
                  label="Arrangement:*"
                  options={optionalFileArrangementOptions}
                />
              </div>
            </div>
            <DescriptionField label="Page Description" name="description" />
            {fieldRestrictions.canSee('query_string') && (
              <>
                <TextField
                  label="Website URL *"
                  name="query_string"
                  type="text"
                  placeholder="example.com"
                  validate={websiteUrlValidator}
                  disabled={
                    (!getIsSuper(activeRole) && editPageSelected && !isPageDuplicated) ||
                    fieldRestrictions.isDisabled('query_string')
                  }
                  parse={value => transformToUrl(value).toLowerCase()}
                  format={value => value?.toLowerCase()}
                  customOnBlur={handleQueryStringBlur}
                />
                {formState.getState().values?.query_string && (
                  <div className="category form-category query-string">
                    {ENV_CONFIG().PARTNER_PAGE}
                    {formState.getState().values?.query_string}
                  </div>
                )}
              </>
            )}
            <RecipientCardsForm
              isPreviewVisible={isPreviewVisible}
              editPageSelected={editPageSelected}
              isPageDuplicated={isPageDuplicated}
            />
            <span className="eyebrow-text inkBlue">Additional customization [optional]</span>
            <TextField
              label="Page Call to Action"
              name="page_cta"
              placeholder="CTA copy"
              type="text"
            />
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 1 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <DonationAmountsForm />
            {getIsSuper(activeRole) && (
              <>
                <DonationAllocationForm
                  isPreviewVisible={isPreviewVisible}
                  editPageSelected={editPageSelected}
                  isPageDuplicated={isPageDuplicated}
                  onEditRecipients={() => {
                    handleClickAccordion(0);
                    setTimeout(() => {
                      document
                        .getElementById('recipient-cards-form')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 550);
                  }}
                />
                <OfflineDonationForm
                  isPreviewVisible={isPreviewVisible}
                  editPageSelected={editPageSelected}
                />
              </>
            )}
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 2 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Custom Disclaimer Text</span>
            <DescriptionField
              label="Custom text that appears at the bottom of the Donation page, above Oath disclaimer."
              name="custom_disclaimer"
              placeholder="This is an optional field"
            />

            {fieldRestrictions.canSee('whitelabeled') && (
              <>
                <span className="oath-eyebrow inkBlue">White label navigation</span>
                <div className="col-md-6 padding0">
                  <RadioField
                    name="whitelabeled"
                    label="Show or hide Oath’s navigation bar on your Donation page:"
                    options={whiteLabelNavigationOptions}
                    disabled={fieldRestrictions.isDisabled('whitelabeled')}
                  />
                </div>
              </>
            )}
            {fieldRestrictions.canSee('is_oath_disclaimer_visible') && (
              <>
                <span className="oath-eyebrow inkBlue">Oath Disclaimer Visibility</span>
                <div className="col-md-12 padding0">
                  <SwitchToggleField
                    name="is_oath_disclaimer_visible"
                    label="If OFF, the Partner’s custom disclaimer should include ALL required disclaimer information."
                  />
                </div>
              </>
            )}
            {fieldRestrictions.canSee('active_page_status') && (
              <>
                <span className="oath-eyebrow inkBlue">Page Status</span>
                <div className="margin0 form-group">
                  <SwitchToggleField
                    name="active_page_status"
                    label="If OFF is selected, your page will be paused."
                  />
                </div>
              </>
            )}
            {!formState.getState().values?.active_page_status && (
              <>
                <TextField
                  label="Pause Description Copy*"
                  name="paused_description"
                  placeholder="100 characters recommended"
                  validate={required}
                />
                <div className="row">
                  <div
                    className={classNames({
                      'col-md-6 padding-left0': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Redirect Button Copy*"
                      name="paused_redirect_button"
                      placeholder="30 characters max"
                      maxLength={30}
                      validate={required}
                    />
                  </div>
                  <div
                    className={classNames({
                      'col-md-6 padding0': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Redirect Button URL (Where should your Paused page go to?)*"
                      name="paused_redirect_url"
                      placeholder="https/www.url.com"
                      validate={required}
                    />
                  </div>
                </div>
              </>
            )}
            {fieldRestrictions.canSee('recurring_enabled') && (
              <>
                <ConfirmationModal
                  isOpen={isRecurringModalOpen}
                  handleSave={() => {
                    formState.change('recurring_enabled', true);
                    setIsRecurringModalOpen(false);
                  }}
                  handleCancel={() => setIsRecurringModalOpen(false)}
                  warningMessage="Once recurring payments are enabled for this page, they cannot be disabled. Are you sure you want to proceed?"
                  confirmButtonText="Enable Recurring Payments"
                />
                <ConfirmationModal
                  isOpen={isRecurringAllocationConflictModalOpen}
                  handleSave={() => {
                    formState.change('recurring_enabled', true);
                    formState.change('allocation_type', 'EVEN');
                    setIsRecurringAllocationConflictModalOpen(false);
                  }}
                  handleCancel={() => setIsRecurringAllocationConflictModalOpen(false)}
                  warningMessage="Recurring payments require Even Split allocation. Enabling recurring will switch your allocation type to Even Split. Do you want to proceed?"
                  confirmButtonText="Yes, switch to Even Split"
                />
                <span className="oath-eyebrow inkBlue">Recurring Payments</span>
                <div className="margin0 form-group">
                  <SwitchToggleField
                    name="recurring_enabled"
                    label="Once enabled, recurring donations cannot be disabled."
                    disabled={!!editPageSelected?.[0]?.recurring_enabled && !isPageDuplicated}
                    onChange={() => {
                      if (
                        !(editPageSelected?.[0]?.recurring_enabled && !isPageDuplicated) &&
                        formState.getState().values?.recurring_enabled
                      ) {
                        formState.change('recurring_enabled', false);
                        if (
                          CUSTOM_ALLOCATION_TYPES.includes(
                            formState.getState().values?.allocation_type
                          )
                        ) {
                          setIsRecurringAllocationConflictModalOpen(true);
                        } else {
                          setIsRecurringModalOpen(true);
                        }
                      }
                    }}
                  />
                </div>
                {formState.getState().values?.recurring_enabled && (
                  <div
                    className={classNames('padding0', {
                      'col-md-6': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <RadioField
                      name="recurring_interval"
                      label="Recurring interval:*"
                      options={recurringIntervalOptions}
                      validate={required}
                    />
                  </div>
                )}
              </>
            )}
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={pagesState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 3 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Community card details</span>
            <div className="row">
              <div className="col-md-6 padding-left0">
                <TextField
                  label="Eyebrow Subtitle Text"
                  name="page_category"
                  placeholder="Appears above card title (35 characters max)"
                  maxLength={35}
                />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-6': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <UploadFileForm type="community" />
              </div>
            </div>
            <div className="row align-center">
              <div
                className={classNames({
                  'col-md-6 padding-left0': !isPreviewVisible,
                  'col-md-12 padding0': isPreviewVisible,
                })}
              >
                <TextField
                  label="Fundraising Goal*"
                  name="fundraising_goal"
                  type="text"
                  validate={required}
                  format={value => {
                    if (!value) return '';
                    const numValue = typeof value === 'string' ? parseFloat(value) : value;
                    return isNaN(numValue) ? value : formatNumberAsCurrency(numValue);
                  }}
                  parse={parseCurrency}
                />
              </div>
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-6': isPreviewVisible,
                })}
              >
                <RadioField
                  name="display_fundraising_bar"
                  label="Display fundraising bar?*"
                  options={displayFundraisingBarOptions}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 padding0">
                <TextField label="Bottom URL" name="learn_more_link" placeholder="Enter URL" />
              </div>
              <div className="col-md-2" />
              <div className="col-md-4 padding0">
                <TextField
                  label="Bottom URL Text (Default: Learn More)"
                  name="bottom_url_text"
                  placeholder="Learn More"
                />
              </div>
            </div>
            <AccordionButton
              type="submit"
              variant="submit"
              disabled={pagesState.isLoading}
              isSubmitting={formState.getState().submitting}
            >
              Publish
            </AccordionButton>
          </div>
        </div>
      )}
    </div>
  );
};
